export const getStatedmarkdown = `
# ServoDesk API Portal

The ServoDesk API enables approved users to integrate external systems with ServoDesk securely and programmatically. Using the API, you can automate common operational workflows—such as retrieving tickets, creating requests, synchronising customers/users, and updating statuses—without needing to use the UI.

---

## Who Can Use the API?

Any user who has been granted access to the API portal and has the appropriate role/permissions in ServoDesk can generate an API token and call the APIs available to them.

The data and operations you can access are governed by your assigned permissions (RBAC).

---

## Typical Use Cases

- Create and update incidents/service requests from external tools  
- Pull ticket lists for reporting and dashboards  
- Sync users/organisations from an identity or HR system  
- Integrate monitoring alerts to create tickets automatically  
- Automate assignment, status changes, and SLA workflows  

---

## Getting Started

### 1. Prerequisites

Before you begin, ensure you have:

- An active ServoDesk user account  
- Access to https://portal.servodesk.com  
- Permissions that allow API usage (and access to relevant modules such as Tickets, Users, and Assets)

---

### 2. Base URL

All API requests use the same base URL:

\`\`\`
{baseUrl}
\`\`\`

**Example**
\`\`\`
https://{your-tenant}.servodesk.com
\`\`\`

If your organisation exposes the API through a dedicated gateway, use the gateway base URL provided by your administrator.

---

## Authentication

ServoDesk APIs are accessed using a token generated in the UI.  
Treat this token like a password.

---

### Token Types (Recommended)

- **Personal Access Token (PAT)**  
  Best for development and user-owned integrations.

- **Service Token / Integration Token**  
  Best for system-to-system integrations (recommended for production), owned by a service account.

If both are available, prefer a service token for production so you can rotate or revoke it without impacting a person’s account.

---

## Generate an API Token in the UI

1. Sign in to https://portal.servodesk.com  
2. Open **User Profile / Account Settings**  
3. Navigate to **API Access / Developer Settings / API Tokens** (label may vary)  
4. Select **Create token / Generate token**  
5. Configure:
   - Token name (e.g., \`PowerBI-Reporting\`, \`Jenkins-Integration\`)
   - Expiry (recommended: set an expiry and rotate regularly)
   - Scopes/Permissions (if prompted) – choose least privilege
6. Click **Generate**
7. Copy the token immediately and store it securely  
   *(The token may only be shown once)*

---

### If You Cannot See “API Tokens”

You likely need one of the following:

- An administrator to enable API access for your role  
- A licence or feature flag enabled for API usage  
- Additional permissions to generate tokens  

---

## Use the Token in API Requests

### Required Header

Include the token on every request using the \`Authorization\` header:

\`\`\`
Authorization: Bearer <YOUR_TOKEN>
\`\`\`

> If your implementation uses a different header name such as \`X-API-Key\` or \`authtoken\`, update the examples accordingly in your portal.

---

## Quick Examples

### Example 1 — Fetch Current User (Sanity Check)

\`\`\`bash
curl -X GET "{baseUrl}/api/v1/me" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Accept: application/json"
\`\`\`

---

### Example 2 — List Tickets

\`\`\`bash
curl -X GET "{baseUrl}/api/v1/tickets?status=open&page=1&pageSize=50" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Accept: application/json"
\`\`\`

---

### Example 3 — Get Ticket Details by ID

\`\`\`bash
curl -X GET "{baseUrl}/api/v1/tickets/12345" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Accept: application/json"
\`\`\`

---

## Postman Setup

### Create a New Environment

\`\`\`
baseUrl = {baseUrl}
token   = <your token>
\`\`\`

### Add Header to Requests

\`\`\`
Authorization = Bearer {{token}}
\`\`\`

### Use Base URL Variable

\`\`\`
GET {{baseUrl}}/api/v1/tickets
\`\`\`

---

## Common Responses and Troubleshooting

### 401 Unauthorized
- Token missing, invalid, or expired  
- Wrong header format (ensure \`Bearer <token>\`)

### 403 Forbidden
- Token is valid, but your role does not have permission for that API or record

### 404 Not Found
- Incorrect endpoint path  
- Record ID does not exist or is not visible to your role

### 429 Too Many Requests
- Rate limit exceeded  
- Reduce request volume and add retries/backoff

---

## Token Security and Best Practices

- Store tokens in a secure secret store (Azure Key Vault, AWS Secrets Manager, Vault, etc.)
- Never hardcode tokens or commit them to source control
- Use expiring tokens and rotate them regularly
- Prefer service accounts/tokens for production integrations
- Revoke tokens immediately if you suspect exposure

---

## Support

If you need access, additional scopes, or help identifying the correct endpoints for your use case, contact your ServoDesk administrator or your designated support channel.
`;



export const markdown = `
## Create New Group
To add a new group, fill out the form and click **Create Group**.
## Group Creation Form
\`\`\`
+-------------------------------+
|       New Group Form          |
+-------------------------------+
| Name:        [___________]    |
| Description: [___________]    |
| Ranking:     [___0___]        |
| Is Active:   [x]              |
|      [Create Group]           |
+-------------------------------+
\`\`\`

**Required fields:**  
- Name, Description, Ranking, Is Active
---
## What Happens After Submit?
1. The user fills the form and clicks **Create Group**.
2. The frontend sends a POST request to the backend:
\`\`\`
  curl -X 'GET' 
  'https://simplisysapi.vizionsys.com/gateway/Group' 
  -H 'accept: */*' 
  -H 'Authorization: Bearer token' 
  -H 'Tenant-Code: tenet-code'
\`\`\`
3. If successful, the backend responds with the new group data (including generated ID).
---
## Security Note
- Only authorized users should create groups.
- Always use HTTPS.
`;

export const agentApiMarkdown = `
## Create New Agent

To add a new agent, fill out the form and click **Create Agent**.

---

## Agent Creation Form

\`\`\`plaintext
+------------------------------+
|       New Agent Form         |
+------------------------------+
| First Name:   [__________]   |
| Last Name:    [__________]   |
| Username:     [__________]   |
| Agent Type:   [___0___]      |
| Mobile Phone: [__________]   |
| Email:        [__________]   |
| Is Admin:     [ ]            |
| Role ID:      [__38___]      |
| Language:     [______]       |
| License Key:  [______]       |
| Description:  [postman]      |
| Avatar ID:    [___0___]      |
| 2FA Enabled:  [ ]            |
| Groups:                     |
|   - Group 1 [ ] (default)    |
|   - Group 2 [x] (default)    |
|   - Group 10[ ] (default)    |
| Password:     [123456]       |
|         [Create Agent]       |
+------------------------------+
\`\`\`

#### **Required fields:**
- First Name, Last Name, Role ID, Description, Password

---

## API Request Example

### **POST \`/gateway/Agent\`**

**Headers:**
\`\`\`
tenant-code: vizionsys
Content-Encrypted: true
Authorization: Bearer <your-token>
Content-Type: application/json
\`\`\`

**Body:**
\`\`\`json
{
  "firstName": "Test ",
  "lastName": "postman",
  "userName": "",
  "agentTypeId": 0,
  "mobilePhone": "",
  "email": "",
  "isAdmin": false,
  "roleId": 38,
  "language": "",
  "licenseKeyId": null,
  "agentDescription": "postman",
  "agentAvatarId": 0,
  "isTwoFactorEnabled": false,
  "userGroupList": [
    { "groupId": 1, "isDefault": false },
    { "groupId": 2, "isDefault": true },
    { "groupId": 10, "isDefault": false }
  ],
  "password": {
    "passwordHash": "123456"
  }
}
\`\`\`

**cURL Example:**
\`\`\`bash
curl -X 'POST' \\
  'https://simplisysapi.vizionsys.com/gateway/Agent' \\
  -H 'tenant-code: vizionsys' \\
  -H 'Content-Encrypted: true' \\
  -H 'Authorization: Bearer <your-token>' \\
  -H 'Content-Type: application/json' \\
  -d '{  
        "firstName": "Test ",
        "lastName": "postman",
        "userName": "",
        "agentTypeId": 0,
        "mobilePhone": "",
        "email": "",
        "isAdmin": false,
        "roleId": 38,
        "language": "",
        "licenseKeyId": null,
        "agentDescription": "postman",
        "agentAvatarId": 0,
        "isTwoFactorEnabled": false,
        "userGroupList": [ 
          { "groupId": 1, "isDefault": false },
          { "groupId": 2, "isDefault": true },
          { "groupId": 10, "isDefault": false }
        ],
        "password": { "passwordHash": "123456" }
      }'
\`\`\`

---

## Parameters

| Name             | Type          | Description                        | Required |
|------------------|--------------|------------------------------------|----------|
| firstName        | string       | First name of the agent            | Yes      |
| lastName         | string       | Last name of the agent             | Yes      |
| userName         | string       | Username for login                 | No       |
| agentTypeId      | number       | Agent type ID                      | No       |
| mobilePhone      | string       | Mobile phone                       | No       |
| email            | string       | Email address                      | No       |
| isAdmin          | boolean      | Is agent admin                     | No       |
| roleId           | number       | Role ID                            | Yes      |
| language         | string       | Preferred language                 | No       |
| licenseKeyId     | number/null  | License Key ID                     | No       |
| agentDescription | string       | Agent description                  | Yes      |
| agentAvatarId    | number       | Agent avatar ID                    | No       |
| isTwoFactorEnabled | boolean    | Is 2FA enabled                     | No       |
| userGroupList    | array        | List of user groups for the agent  | No       |
| password.passwordHash | string  | Agent password                     | Yes      |

---

## Success Response

\`\`\`json
{
  "id": 1,
  "firstName": "Amith",
  "lastName": "Malipatil",
  "userName": "vizion123",
  "agentTypeId": 0,
  "agentAvatarPath": "/imagedetails/vizionsys/Agent/1/vizion123.jpg",
  "mobilePhone": "",
  "email": null,
  "isAdmin": false,
  "isActive": true,
  "roleId": 1,
  "appDeviceId": null,
  "webDeviceId": "cFO5J-9MI-XFPFxWg2JzRx:--",
  "language": "",
  "agentDescription": null,
  "accountStatus": "Completed",
  "createdAt": "2025-01-03T07:12:59.249021",
  "updatedAt": "2025-07-16T14:07:33.751756",
  "isTwoFactorEnabled": false,
  "isTwoFactorSetupCompleted": false,
  "agentAvatarId": 0,
  "licenseKeyId": 1,
  "agentPreferences": "{\"theme\": \"light-mode\",\"view\": \"Status\"}",
  "userGroupList": [
    {
      "id": 620370,
      "userId": 1,
      "groupId": 1,
      "isDefault": true,
      "isActive": true,
      "agent": null
    }
  ],
  "password": null,
  "roles": {
    "id": 1,
    "name": "SuperAdmin",
    "description": "SuperAdmin",
    "isActive": true,
    "isSuperAdmin": true,
    "isSSPRole": false,
    "agentPermissionsList": null
  },
  "agentLoginSession": null
}
\`\`\`

## Error Response

\`\`\`json
{
  "error": "Invalid request",
  "message": "Missing required fields"
}
\`\`\`

---

## Security Note

- Only authorized users should create agents.
- Always use HTTPS.
`.trim();


export const organisationBySubdomain = `
## Get Organisation by Subdomain

Retrieves organisation details using the organisation's subdomain.

---

### HTTP Request

\`\`\`http
GET /gateway/master/Organisation/by/
subdomain?subdomain={subdomain}
Host: simplisysapi.vizionsys.com
\`\`\`

**Full URL Example:**  
\`https://simplisysapi.vizionsys.com/gateway/master
/Organisation/by/subdomain?subdomain=simplisys\`


---

### cURL Example

\`\`\`bash
curl -X GET "https://simplisysapi.vizionsys.com/gateway/
master/Organisation/by/subdomain?subdomain=simplisys" \\
  -H "tenant-code: vizionsys" \\
  -H "Authorization: Bearer <your-token>"
\`\`\`

---

### Success Response

\`\`\`json
{
  "tenantcode": "vizionsys",
  "provisioningStatus": "SUCCESS",
  "organizationName": "Simplisys"
}
\`\`\`

---

### Error Response

\`\`\`json
{
  "error": "Organisation not found",
  "message": "No organisation matches the given subdomain."
}
\`\`\`

---

### Security Note

- You must provide a valid Bearer token in the "Authorization" header.
- Always use HTTPS.
`


export const LoginOrganisation = `
# User Authentication

To access protected resources, users must authenticate using their email and password.

## Login Form Structure

\`\`\`
+-----------------------------+
|        Authentication       |
+-----------------------------+
| Email:     [______________] |
| Password:  [______________] |
|  "RECAPTCHA_TOKEN"          |
|                            |
|       [ Authenticate ]      |
+-----------------------------+
\`\`\`

**Form Fields:**
- **Email:** Your registered email address
- **Password:** Your secure password
- **Authenticate:** Submit button to initiate login

---

## Authentication Flow

1. The frontend first retrieves the organization details:
   \`\`\`
   GET /gateway/master/Organisation/by/subdomain?subdomain=simplisys
   \`\`\`

2. The response contains the tenant code:
   \`\`\`json
   {
     "tenantcode": "vizionsys"
   }
   \`\`\`

3. The frontend stores the tenant code in localStorage and includes it in the login request:
   \`\`\`json
   POST /api/auth/login
   Content-Type: application/json

   {
     "email": "user@example.com",
     "password": "securePassword123",
     "RECAPTCHA_TOKEN": "RECAPTCHA_TOKEN",
     "tenantcode": "vizionsys"
   }
   \`\`\`

4. Upon successful authentication, the server responds with a JWT token:
   \`\`\`json
   {
     "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
   }
   \`\`\`

5. The frontend stores this token in localStorage:
   \`\`\`javascript
   localStorage.setItem('jwtToken', token);
   \`\`\`

6. Subsequent requests include the token in the Authorization header:
   \`\`\`
   Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
   \`\`\`

---

## Security Best Practices

- Always use HTTPS for all communications
- Never expose JWT tokens in client-side code
- Implement proper token expiration and refresh mechanisms
- Use secure, HTTP-only cookies when possible
- Regularly audit your authentication flow
`;
export const ticketNotificationsMarkdown = `
## View Ticket Notifications

To view ticket notifications, provide the required query parameters and send a GET request.

## Ticket Notifications Endpoint

\`\`\`
+----------------------------------------------------+
|           Ticket Notifications Search              |
+----------------------------------------------------+
| Page:         [____1____]                          |
| Count:        [___75___]                           |
| tenant-code:  [vizionsys] (header)                 |
| Authorization:[Bearer <token>] (header)            |
|       [ Get Notifications ]                        |
+----------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)
- \`Authorization\` (Bearer token, request header)
- \`page\` (query param)
- \`count\` (query param)

---

## What Happens On Request?

1. The user submits the page and count (pagination) and authenticates.
2. The frontend sends a GET request:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/TicketNotification?page=1&count=75' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with a list of notifications.

---

## Example Success Response

\`\`\`json
{
  "page": 1,
  "count": 75,
  "total": 150,
  "notifications": [
    {
      "id": 12345,
      "ticketId": 6789,
      "message": "Ticket updated",
      "createdAt": "2024-05-23T10:31:42Z",
      "read": false
    }
    // ...additional notifications...
  ]
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- Only authenticated users can view ticket notifications.
- Always use HTTPS.
`;

export const subscriptionListMarkdown = `
## View Subscription List

To retrieve all subscriptions for the tenant, simply send a GET request with the required header.

## Subscription List Endpoint

\`\`\`
+-----------------------------------------------+
|            Subscription List Search           |
+-----------------------------------------------+
| tenant-code: [vizionsys] (header)             |
|       [ Get Subscriptions ]                   |
+-----------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)

---

## What Happens On Request?

1. The user (or system) triggers a subscription list fetch.
2. The frontend sends a GET request:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/subscription' \\
  --header 'tenant-code: vizionsys'
\`\`\`
3. If successful, the backend responds with an array of subscription objects.

---

## Example Success Response

\`\`\`json
{
  "subscriptions": [
    {
      "id": 1,
      "planName": "Pro",
      "status": "Active",
      "startDate": "2024-01-01",
      "endDate": "2025-01-01",
      "features": [
        "Unlimited Tickets",
        "Priority Support"
      ]
    }
    // ...more subscriptions...
  ]
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Missing or invalid tenant-code"
}
\`\`\`

---

## Security Note
- Only authorized tenants can view subscription information.
- Always use HTTPS.
`;

export const agentByTokenMarkdown = `
## View Agent Profile by Token

To retrieve the agent's profile using an access token, send a GET request to the endpoint below with the required headers.

## Agent Profile Endpoint

\`\`\`
+--------------------------------------------------+
|             Get Agent Profile (Token)            |
+--------------------------------------------------+
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|             [ Get Agent Profile ]                |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)
- \`Authorization\` (Bearer token, request header)

---

## What Happens On Request?

1. The system or user sends a GET request to get agent details tied to the current token.
2. The frontend sends:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Agent/by/token/v2' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with the agent's profile.

---

## Example Success Response

\`\`\`json
{
  "agent": {
    "id": 1,
    "firstName": "Amith",
    "lastName": "Malipatil",
    "userName": "vizion123",
    "agentTypeId": 0,
    "agentAvatarPath": "https://simplisysapi.vizionsys.com/gateway/imagedetails/vizionsys/Agent/1/vizion123.jpg",
    "mobilePhone": "",
    "email": null,
    "isAdmin": false,
    "isActive": true,
    "roleId": 1,
    "appDeviceId": null,
    "webDeviceId": "fIgpOFHGTEugQJHliBuxTF:APA91bGtdKpr9QhrPmvvuIlgjayf5m96qDNOmoIq2VMedYQBYyuyIrMyAu3X0ABs3bU79hQUvcvLRxqfAVF95xbmWoYrP1ywFY6QZ3ATAQ8OKS1y_Ku-vLE",
    "language": "",
    "agentDescription": null,
    "accountStatus": "Completed",
    "createdAt": "2025-01-03T07:12:59.249021",
    "updatedAt": "2025-07-16T14:07:33.751756",
    "isTwoFactorEnabled": false,
    "isTwoFactorSetupCompleted": false,
    "agentAvatarId": 0,
    "licenseKeyId": 1,
    "agentPreferences": "{\"theme\": \"light-mode\",\"view\": \"Status\"}",
    "userGroupList": [
      {
        "id": 620370,
        "userId": 1,
        "groupId": 1,
        "isDefault": true,
        "isActive": true,
        "agent": null
      }
    ],
    "password": null,
    "roles": {
      "id": 1,
      "name": "SuperAdmin",
      "description": "SuperAdmin",
      "isActive": true,
      "isSuperAdmin": true,
      "isSSPRole": false,
      "agentPermissionsList": []
    },
    "agentLoginSession": null
  },
  "organisation": {
    "id": 1,
    "name": "Vizionsys",
    "email": "amith.malipatil404@gmail.com",
    "mobile": "9980878194",
    "editionId": 3,
    "totalNumberOfAgents": 22,
    "licenseStartDate": "2024-05-20T09:24:32.991978",
    "licenseEndDate": "2025-06-20T09:24:32.991978",
    "createdAt": "2024-01-10T09:01:27.745163",
    "updatedAt": "2024-09-26T12:23:47.163137",
    "isActive": true,
    "isYearly": false,
    "isMonthly": true,
    "country": "INDIA",
    "organisationAvatarId": 9748,
    "organisationAvatarPath": "https://simplisysapi.vizionsys.com/gateway/imagedetails/webasserts/vizionsys.png",
    "subdomain": "simplisys.vizionsys.com",
    "location": null,
    "primaryAgent": null,
    "organisationCoreProduct": null,
    "organisationUrl": null
  },
  "organisationSetting": {
    "id": 1,
    "isTwoFactorEnabled": false,
    "dateFormat": "en-gb",
    "createdAt": "2024-01-10T09:01:22.445784",
    "updatedAt": "2024-01-10T09:01:22.445784"
  }
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or expired Bearer token"
}
\`\`\`

---

## Security Note
- Only valid tokens can access agent profiles.
- Always use HTTPS.
`;



export const getRoleByIdMarkdown = `
## View Role Details by ID

To retrieve specific role details, send a GET request to the endpoint with the required headers.

## Role Details Endpoint

\`\`\`
+--------------------------------------------------+
|           Get Role Info (By ID)                  |
+--------------------------------------------------+
| Role ID:        [1]   (in URL path)              |
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|             [ Get Role Details ]                 |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (path parameter)
- \`tenant-code\` (request header)
- \`Authorization\` (Bearer token, request header)

---

## What Happens On Request?

1. The system/user sends a GET request for a specific role by ID.
2. The frontend sends:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Role/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with the role details.

---

## Example Success Response

\`\`\`json
{
  "id": 1,
  "name": "SuperAdmin",
  "description": "SuperAdmin",
  "isActive": true,
  "isSuperAdmin": true,
  "isSSPRole": false,
  "agentPermissionsList": []
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Role Not Found",
  "message": "No role exists with the specified ID"
}
\`\`\`

---

## Security Note
- Only authorized users may view role details.
- Always use HTTPS.
`;

export const dashboardUserGroupsMarkdown = `
## View Dashboard User Groups

To fetch all user groups available for the dashboard, send a GET request to the endpoint with the required headers.

## Dashboard User Groups Endpoint

\`\`\`
+--------------------------------------------------+
|          Get Dashboard User Groups (v4)          |
+--------------------------------------------------+
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|            [ Get User Groups ]                   |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)
- \`Authorization\` (Bearer token, request header)

---

## What Happens On Request?

1. A GET request is sent to fetch dashboard user groups.
2. The frontend sends:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Dashboard/usergroup/v4' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with an array of group objects.

---

## Example Success Response

\`\`\`json
[
 
  "group": {
    "Proceed for Disabled": {
      "count": 0,
      "agentList": {
        "Sanjay Kulkarni": {
          "id": 77,
          "count": 0
        },
        "Ashu Ash": {
          "id": 156,
          "count": 0
        },
        "Ragul L": {
          "id": 160,
          "count": 0
        },
        "sanj20212 Vizionsys123": {
          "id": 18,
          "count": 0
        },
        "Ash Ashwini": {
          "id": 6,
          "count": 0
        }
      }
    }
  }

  // ...more groups
]
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- You must provide a valid JWT Bearer token in the Authorization header.
- Always use HTTPS.
`;

export const dashboardV3Markdown = `
## View Dashboard Data (v3)

To retrieve dashboard data with enhanced security, send an encrypted POST payload to this endpoint along with the required headers.

## Dashboard Data Endpoint

\`\`\`
+----------------------------------------------------+
|                Dashboard Data (v3)                 |
+----------------------------------------------------+
| tenant-code:    [vizionsys]      (header)          |
| Content-Type:   [text/plain]      (header)         |
| Authorization:  [Bearer <token>]  (header)         |
| Body:           [Encrypted String]                 |
|               [ Get Dashboard ]                    |
+----------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  
- \`Content-Type\`: \`text/plain\`  
- Request body must be an encrypted text string

---

## What Happens On Request?

1. The frontend encrypts the payload and sends a POST request with it as plain text.
2. The request is sent as:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Dashboard/v3' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_body>'
\`\`\`
3. If successful, the backend responds with decrypted dashboard data.

---

## Example Success Response

\`\`\`json
{
  "ticketStatus": {
    "newTicketCount": 2,
    "openTicketCount": 2035,
    "dataList": {
      "NewtoMe": {
        "id": 2,
        "count": 1429
      }
    }
  },
  "priority": {
    "P1": {
      "id": 1,
      "count": 922,
      "color": "#ec0909"
    }
  },
  "tickettype": {
    "Incident": {
      "id": 50,
      "count": 905
    }
  },
  "resolvedToday": 0,
  "dueToday": 1,
  "dueInWeek": 2,
  "overdue": 1287,
  "responseSlainpercentageToday": 0,
  "resolutionSlainpercentageToday": 0,
  "resolvedTodayPer": 0
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- The request body must be encrypted.
- Only use HTTPS.
- Valid authentication is required.
`;

export const groupListMarkdown = `
## View Group List

To fetch the full list of groups for the current tenant, send a GET request with the required headers.

## Group List Endpoint

\`\`\`
+--------------------------------------------------+
|                  Get Group List                  |
+--------------------------------------------------+
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|                 [ Get Groups ]                   |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  

---

## What Happens On Request?

1. The frontend sends a GET request for all group records.
2. Example request:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Group' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with a list of groups.

---

## Example Success Response

\`\`\`json
[
  
  {
    "id": 1,
    "name": "1st Line",
    "ticketAssignWithin": null,
    "escalationEmailTo": null,
    "groupDescription": null,
    "ranking": 0,
    "isActive": true,
    "createdAt": "2024-01-10T09:01:22.445784",
    "updatedAt": "2025-05-08T07:15:52.868795",
    "userGroupList": [
      {
        "id": 618325,
        "userId": 106,
        "groupId": 1,
        "isDefault": false,
        "isActive": true,
        "agent": {
          "id": 106,
          "firstName": "Shammmmmmmiiiiiii",
          "lastName": "S",
          "userName": "sham5178",
          "agentTypeId": 0,
          "agentAvatarPath": null,
          "mobilePhone": "",
          "email": "",
          "isAdmin": false,
          "isActive": false,
          "roleId": 2,
          "appDeviceId": null,
          "webDeviceId": null,
          "language": "",
          "agentDescription": "",
          "accountStatus": "New",
          "createdAt": "2024-10-17T10:18:34.318659",
          "updatedAt": "2024-10-17T10:18:34.318659",
          "isTwoFactorEnabled": false,
          "isTwoFactorSetupCompleted": false,
          "agentAvatarId": 0,
          "licenseKeyId": null,
          "agentPreferences": null,
          "userGroupList": null,
          "password": null,
          "roles": null,
          "agentLoginSession": null
        }
      }
    ]
  }

  // ...more groups
]
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- Only authenticated users can view groups.
- Always use HTTPS.
`;

export const groupGetByIdMarkdown = `
## View Group Details by ID

Fetch details for a single group by its unique ID.

## Group Details Endpoint

\`\`\`
+--------------------------------------------------+
|                Get Group By ID                   |
+--------------------------------------------------+
| Group ID:       [1]         (in URL path)        |
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|               [ Get Group Details ]              |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  

---

## What Happens On Request?

1. The frontend sends a GET request for a specific group by ID.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Group/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with group data.

---

## Example Success Response

\`\`\`json
{
  "id": 1,
  "name": "1st Line",
  "ticketAssignWithin": null,
  "escalationEmailTo": null,
  "groupDescription": null,
  "ranking": 0,
  "isActive": true,
  "createdAt": "2024-01-10T09:01:22.445784",
  "updatedAt": "2025-05-08T07:15:52.868795",
  "userGroupList": [
    {
      "id": 618184,
      "userId": 78,
      "groupId": 1,
      "isDefault": false,
      "isActive": true,
      "agent": {
        "id": 78,
        "firstName": "Harsha",
        "lastName": "Patel",
        "userName": "hars8762",
        "agentTypeId": 0,
        "agentAvatarPath": null,
        "mobilePhone": "919916640814",
        "email": "harsha.patel@vizionsys.com",
        "isAdmin": false,
        "isActive": false,
        "roleId": 1,
        "appDeviceId": null,
        "webDeviceId": null,
        "language": "Kannada",
        "agentDescription": "",
        "accountStatus": "New",
        "createdAt": "2024-10-07T08:56:00.348726",
        "updatedAt": "2024-10-07T08:56:00.348726",
        "isTwoFactorEnabled": false,
        "isTwoFactorSetupCompleted": false,
        "agentAvatarId": 0,
        "licenseKeyId": null,
        "agentPreferences": null,
        "userGroupList": null,
        "password": null,
        "roles": null,
        "agentLoginSession": null
      }
    }
  ]
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Group Not Found",
  "message": "No group exists with the specified ID"
}
\`\`\`

---

## Security Note
- Only authenticated users can view group information.
- Always use HTTPS.
`;


export const groupCreateMarkdown =`
## Create New Group
To add a new group, fill out the form and click **Create Group**.
## Group Creation Form
\`\`\`
+-------------------------------+
|       New Group Form          |
+-------------------------------+
| Name:        [___________]    |
| Description: [___________]    |
| Ranking:     [___0___]        |
| Is Active:   [x]              |
|      [Create Group]           |
+-------------------------------+
\`\`\`

**Required fields:**  
- Name, Description, Ranking, Is Active
---
## What Happens After Submit?
1. The user fills the form and clicks **Create Group**.
2. The frontend sends a POST request to the backend:
\`\`\`
  curl -X 'GET' 
  'https://simplisysapi.vizionsys.com/gateway/Group' 
  -H 'accept: */*' 
  -H 'Authorization: Bearer token' 
  -H 'Tenant-Code: tenet-code'
\`\`\`
3. If successful, the backend responds with the new group data (including generated ID).
---
## Security Note
- Only authorized users should create groups.
- Always use HTTPS.
`;


export const groupSoftDeleteMarkdown = `
## Soft Delete Group (Change Status)

This operation allows you to "soft delete" (typically deactivate) one or more groups by specifying their IDs as a query parameter.

## Soft Delete Group Endpoint

\`\`\`
+----------------------------------------------------------------+
|                Soft Delete Group (Change Status)               |
+----------------------------------------------------------------+
| ids:           [48]       (query parameter)                    |
| tenant-code:   [vizionsys]   (header)                          |
| Authorization: [Bearer <token>] (header)                       |
|               [ Soft Delete Group(s) ]                         |
+----------------------------------------------------------------+
\`\`\`

**Required:**  
- \`ids\` (query parameter; one or more comma-separated group IDs)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)

---

## What Happens On Request?

1. The system/user sends a DELETE request for one or more group IDs.
2. Example:
\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Group/status/change?ids=48' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend will respond indicating which group IDs were updated.

---

## Example Success Response

\`\`\`json
{
  "message": "Groups status updated successfully",
  "updatedIds": [48]
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid IDs",
  "message": "One or more group IDs are invalid or not found"
}
\`\`\`

---

## Security Note
- Only authorized users may update group statuses.
- Always use HTTPS.
`;

export const groupUpdateByIDMarkdown = `
## Update Group By ID

To update a group, send an **encrypted** payload via a PUT request to the endpoint below, including all required headers.

## Group Update Endpoint

\`\`\`
+--------------------------------------------------------+
|                   Update Group By ID                   |
+--------------------------------------------------------+
| Group ID:       [48]              (in URL path)        |
| tenant-code:    [vizionsys]        (header)            |
| Content-Type:   [text/plain]        (header)           |
| Authorization:  [Bearer <token>]    (header)           |
| Body:           [Encrypted Payload]                    |
|                    [ Update Group ]                    |
+--------------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  
- \`Content-Type:\` \`text/plain\`  
- Body must be an encrypted JSON string

---

## What Happens On Request?

1. The frontend sends the updated group details, encrypted, as the request body.
2. Example:
\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Group/48' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`
3. If successful, the backend responds with the updated group object.

---

## Example Decrypted Payload

\`\`\`json
{
  "name": "Testing group postman",
  "groupDescription": "Testing group postman",
  "ranking": 999,
  "isActive": true,
  "updatedAt": "2025-07-18T12:00:00.000Z"
}
\`\`\`

---

## Example Success Response

\`\`\`json
{
  "id": 48,
  "name": "Testing group postman",
  "ticketAssignWithin": null,
  "escalationEmailTo": null,
  "groupDescription": "Testing group postman",
  "ranking": 999,
  "isActive": true,
  "createdAt": "2025-07-17T10:44:37.7349927Z",
  "updatedAt": "2025-07-18T12:00:00.000Z",
  "userGroupList": null
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body is missing required fields or not encrypted"
}
\`\`\`

---

## Security Note
- Payloads must be encrypted on the client.
- Use HTTPS at all times.
- Only authorized users can update groups.
`;


export const roleListMarkdown = `
## View Role List

Retrieve all available roles for the current tenant.

## Role List Endpoint

\`\`\`
+--------------------------------------------------+
|                  Get Role List                   |
+--------------------------------------------------+
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|                [ Get Roles ]                     |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  

---

## What Happens On Request?

1. The frontend sends a GET request for all role records.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Role' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with a list of roles.

---

## Example Success Response

\`\`\`json
[
  {
    "roleId": 1,
    "name": "Admin",
    "isActive": true,
    "permissions": [
      "MANAGE_USERS",
      "VIEW_REPORTS"
    ]
  },
  {
    "roleId": 2,
    "name": "Support",
    "isActive": true,
    "permissions": [
      "HANDLE_TICKETS"
    ]
  }
  // ...more roles
]
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- Only authorized users can view roles.
- Always use HTTPS.
`;

export const roleGetByIdMarkdown = `
## View Role Details by ID

Fetch details for a specific role by its unique ID.

## Role Details Endpoint

\`\`\`
+--------------------------------------------------+
|                Get Role By ID                    |
+--------------------------------------------------+
| Role ID:        [1]         (in URL path)        |
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|              [ Get Role Details ]                |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)

---

## What Happens On Request?

1. The frontend sends a GET request for a specific role by ID.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Role/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with role data.

---

## Example Success Response

\`\`\`json
{
  "roleId": 1,
  "name": "Admin",
  "isActive": true,
  "description": "Administrator role",
  "permissions": [
    "MANAGE_USERS",
    "VIEW_REPORTS"
  ]
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Role Not Found",
  "message": "No role exists with the specified ID"
}
\`\`\`

---

## Security Note
- Only authenticated users can view role information.
- Always use HTTPS.
`;

export const roleCreateMarkdown = `
## Create New Role

To create a new role, send an **encrypted** payload via a POST request to the endpoint below, including all required headers.

## Role Creation Endpoint

\`\`\`
+--------------------------------------------------------+
|                  Create New Role                       |
+--------------------------------------------------------+
| tenant-code:    [vizionsys]       (header)             |
| Content-Type:   [text/plain]       (header)            |
| Authorization:  [Bearer <token>]   (header)            |
| Body:           [Encrypted Payload]                    |
|                  [ Create Role ]                       |
+--------------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  
- \`Content-Type:\` \`text/plain\`  
- Body must be an encrypted JSON string

---

## What Happens On Request?

1. The frontend sends new role details, encrypted, as the request body.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Role' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`
3. If successful, the backend responds with the new role object and its new ID.

---

## Example Decrypted Payload

\`\`\`json
{
  "name": "QA",
  "description": "Quality assurance role",
  "permissions": ["TEST_FEATURES", "VIEW_BUGS"],
  "isActive": true,
  "createdAt": "2025-07-18T15:00:00.000Z"
}
\`\`\`

---

## Example Success Response

\`\`\`json
{
  "roleId": 5,
  "name": "QA",
  "description": "Quality assurance role",
  "permissions": [
    "TEST_FEATURES",
    "VIEW_BUGS"
  ],
  "isActive": true,
  "createdAt": "2025-07-18T15:00:00.000Z"
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body is missing required fields or not encrypted"
}
\`\`\`

---

## Security Note
- Payloads must be encrypted on the client.
- Use HTTPS at all times.
- Only authorized users can create roles.
`;
export const roleUpdateMarkdown = `
## Update Role By ID

To update a role, send an **encrypted** payload via a PUT request to the endpoint below, including all required headers.

## Role Update Endpoint

\`\`\`
+--------------------------------------------------------+
|                      Update Role                       |
+--------------------------------------------------------+
| Role ID:        [59]               (in URL path)       |
| tenant-code:    [vizionsys]        (header)            |
| Content-Type:   [text/plain]        (header)           |
| Authorization:  [Bearer <token>]    (header)           |
| Body:           [Encrypted Payload]                    |
|                    [ Update Role ]                     |
+--------------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  
- \`Content-Type: text/plain\`  
- Body must be an encrypted JSON string

---

## What Happens On Request?

1. The frontend sends updated role details, encrypted, as the request body.
2. Example:
\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Role/59' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`
3. If successful, the backend responds with the updated role object.

---

## Example Decrypted Payload

\`\`\`json
{
  "name": "QA Engineer",
  "description": "Updated QA role",
  "permissions": ["RUN_TESTS", "REPORT_BUGS"],
  "isActive": true,
  "updatedAt": "2025-07-18T17:20:00.000Z"
}
\`\`\`

---

## Example Success Response

\`\`\`json
{
  "roleId": 59,
  "name": "QA Engineer",
  "description": "Updated QA role",
  "permissions": [
    "RUN_TESTS",
    "REPORT_BUGS"
  ],
  "isActive": true,
  "createdAt": "2025-07-10T09:55:00.000Z",
  "updatedAt": "2025-07-18T17:20:00.000Z"
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body is missing required fields or not encrypted"
}
\`\`\`

---

## Security Note
- Payloads must be encrypted on the client.
- Use HTTPS at all times.
- Only authorized users can update roles.
`;
export const roleBulkSoftDeleteMarkdown = `
## Bulk Soft Delete Roles

Soft delete (or deactivate) multiple roles by specifying their IDs and the desired status as query parameters.

## Bulk Soft Delete Roles Endpoint

\`\`\`
+---------------------------------------------------------------+
|                    Bulk Soft Delete Roles                     |
+---------------------------------------------------------------+
| ids:           [1,2,3]      (query parameter)                 |
| status:        [false]      (query parameter)                 |
| tenant-code:   [vizionsys]  (header)                          |
| Authorization: [Bearer <token>] (header)                      |
|                [ Bulk Soft Delete Roles ]                     |
+---------------------------------------------------------------+
\`\`\`

**Required:**  
- \`ids\` (query parameter, comma-separated list of role IDs)  
- \`status\` (query parameter, usually 'false' to deactivate)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  

---

## What Happens On Request?

1. The system/user sends a DELETE request for one or more role IDs, with the target status.
2. Example:
\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Role/soft/bulk/delete?ids=1&status=false' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend will respond indicating which role IDs were updated.

---

## Example Success Response

\`\`\`json
{
  "message": "Roles updated successfully",
  "updatedIds": [1]
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid IDs or status",
  "message": "Invalid or missing role IDs or status"
}
\`\`\`

---

## Security Note
- Only authorized users may update role statuses.
- Always use HTTPS.
`;

export const agentListMarkdown = `
## View Agent List

Retrieve all agents for the current tenant.

## Agent List Endpoint

\`\`\`
+--------------------------------------------------+
|                  Get Agent List                  |
+--------------------------------------------------+
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|                 [ Get Agents ]                   |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  

---

## What Happens On Request?

1. The frontend sends a GET request to retrieve all agent records.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Agent' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with a list of agent objects.

---

## Example Success Response

\`\`\`json
[
  {
    "agentId": 101,
    "firstName": "Jane",
    "lastName": "Doe",
    "email": "jane.doe@company.com",
    "isActive": true,
    "roles": ["Support", "Admin"]
  },
  {
    "agentId": 102,
    "firstName": "John",
    "lastName": "Smith",
    "email": "john.smith@company.com",
    "isActive": false,
    "roles": ["Support"]
  }
  // ...more agents
]
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- Only authorized users can view agents.
- Always use HTTPS.
`;

export const agentGetByIdMarkdown = `
## View Agent Details by ID

Fetch details for a specific agent by their unique ID.

## Agent Details Endpoint

\`\`\`
+--------------------------------------------------+
|                Get Agent By ID                   |
+--------------------------------------------------+
| Agent ID:      [1]         (in URL path)         |
| tenant-code:   [vizionsys]   (header)            |
| Authorization: [Bearer <token>] (header)         |
|             [ Get Agent Details ]                |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)

---

## What Happens On Request?

1. The frontend sends a GET request for a specific agent by ID.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Agent/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with details for that agent.

---

## Example Success Response

\`\`\`json
{
  "agentId": 1,
  "firstName": "Jane",
  "lastName": "Doe",
  "email": "jane.doe@company.com",
  "isActive": true,
  "roles": ["Support", "Admin"],
  "mobile": "+1234567890"
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Agent Not Found",
  "message": "No agent exists with the specified ID"
}
\`\`\`

---

## Security Note
- Only authenticated users can view agent information.
- Always use HTTPS.
`;

export const agentCreateMarkdown = `
## Create New Agent

This API is used to **create a new agent (user)** in the system.

You send the agent details to the backend, and the system creates the agent and returns the result.

---

## API Endpoint

**POST**
\`https://simplisysapi.vizionsys.com/gateway/Agent\`

---

## Required Headers

- **tenant-code** – Your organization code  
- **Authorization** – Bearer access token  
- **Content-Type** – \`text/plain\`  
- **Body** – Encrypted agent data

---

## How It Works (Simple Steps)

1. Fill in agent details (name, email, role, group, password).
2. Encrypt the agent data on the client.
3. Send the encrypted data to the API.
4. The backend creates the agent.
5. A success response is returned with the agent ID.

---

## Sample Request (Encrypted)

\`\`\`bash
curl -X POST 'https://simplisysapi.vizionsys.com/gateway/Agent' \\
  -H 'tenant-code: vizionsys' \\
  -H 'Content-Type: text/plain' \\
  -H 'Authorization: Bearer <token>' \\
  -d '<encrypted_payload>'
\`\`\`

---

## Sample Agent Data (Before Encryption)

\`\`\`json
{
  "firstName": "Test",
  "lastName": "Agent",
  "email": "test@company.com",
  "mobilePhone": "9876543210",
  "roleId": 38,
  "isAdmin": false,
  "userGroupList": [
    { "groupId": 2, "isDefault": true }
  ],
  "password": {
    "passwordHash": "123456"
  }
}
\`\`\`

---

## Success Response (Example)

\`\`\`json
{
  "id": 163,
  "userName": "Test2104",
  "accountStatus": "New",
  "isActive": false,
  "createdAt": "2025-07-18T09:09:01Z"
}
\`\`\`

---

## Error Response (Example)

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Missing required fields or payload not encrypted"
}
\`\`\`

---

## Security Notes

- Always encrypt the request body.
- Use HTTPS for all requests.
- Never share access tokens or passwords.
- Only authorized users can create agents.
`;


export const agentUpdateMarkdown = `
## Update Agent By ID

To update an agent, send an **encrypted** payload via a PUT request to the endpoint below, including all required headers.

## Agent Update Endpoint

\`\`\`
+--------------------------------------------------------+
|                    Update Agent By ID                  |
+--------------------------------------------------------+
| Agent ID:       [5]                (in URL path)       |
| tenant-code:    [vizionsys]         (header)           |
| Content-Type:   [text/plain]         (header)          |
| Authorization:  [Bearer <token>]     (header)          |
| Body:           [Encrypted Payload]                    |
|                 [ Update Agent ]                       |
+--------------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  
- \`Content-Type:\` \`text/plain\`  
- Body must be an encrypted JSON string

---

## What Happens On Request?

1. The frontend sends updated agent details, encrypted, as the request body.
2. Example:
\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Agent/5' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`
3. If successful, the backend responds with the updated agent object.

---

## Example Decrypted Payload

\`\`\`json
{
  "id": 5,
  "firstName": "Peter",
  "lastName": "Lench",
  "userName": "pete2056",
  "agentTypeId": 0,
  "agentAvatarPath": "https://simplisysapi.vizionsys.com/gateway/imagedetails/vizionsys/PLLinkedIN2.png",
  "mobilePhone": "07879 443790",
  "email": "peter.lench@simplisys.com",
  "isAdmin": false,
  "isActive": true,
  "roleId": 4,
  "appDeviceId": null,
  "webDeviceId": "fIiyw-KdI5A-LKlR-W1vOh:APA91bGknRK3c8URmQ8bdmDYnuepTFrOAgH_oqW0KXkmH_qFwqTtl1r_JvAPDRwUQ8o8Ab_7pRF8TMolE_Mur-56CQGzbl6fXS0lcqMHswcMqtCpEuQOn8s",
  "language": null,
  "agentDescription": null,
  "accountStatus": "Completed",
  "createdAt": "2025-01-03T07:51:16.563104",
  "updatedAt": "2025-06-02T11:15:32.846303",
  "isTwoFactorEnabled": false,
  "isTwoFactorSetupCompleted": false,
  "agentAvatarId": 0,
  "licenseKeyId": 6,
  "agentPreferences": "{\"theme\": \"light-mode\",\"view\": \"Status\"}",
  "userGroupList": [
    {
      "groupId": 4,
      "isDefault": true
    },
    {
      "groupId": 7,
      "isDefault": false
    },
    {
      "groupId": 10,
      "isDefault": false
    }
  ],
  "roles": null,
  "agentLoginSession": null,
  "profilePicturePath": "https://simplisysapi.vizionsys.com/gateway/imagedetails/vizionsys/PLLinkedIN2.png"
}
\`\`\`

---

## Example Success Response

\`\`\`json
{
  "agentId": 5,
  "firstName": "Tom",
  "lastName": "Johnson",
  "email": "tom.johnson@company.com",
  "mobile": "+1234567899",
  "roles": ["Support"],
  "isActive": false,
  "updatedAt": "2025-07-18T15:20:00.000Z"
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body missing required fields or not encrypted"
}
\`\`\`

---

## Security Note
- Payloads must be encrypted on the client.
- Use HTTPS at all times.
- Only authorized users can update agents.
`;

export const agentStatusChangeMarkdown = `
## Change Agent Status (Soft Delete)

Change or deactivate an agent by specifying the agent ID and desired status in query parameters.

## Agent Status Change Endpoint

\`\`\`
+-------------------------------------------------------+
|                Change Agent Status                    |
+-------------------------------------------------------+
| id:          [160]          (query parameter)         |
| status:      [false]        (query parameter)         |
| tenant-code: [vizionsys]    (header)                  |
| Authorization: [Bearer <token>] (header)              |
|              [ Change Agent Status ]                  |
+-------------------------------------------------------+
\`\`\`

**Required:**  
- \`id\` (query parameter, Agent ID)  
- \`status\` (query parameter, e.g. 'false' to deactivate)  
- \`tenant-code\` (header)  
- \`Authorization\` (Bearer token, header)  

---

## What Happens On Request?

1. The system sends a DELETE request to change the agent’s status.
2. Example:
\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Agent/status/change?id=160&status=false' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with confirmation.

---

## Example Success Response

\`\`\`json
{
  "message": "Agent status updated successfully",
  "updatedId": 160,
  "newStatus": false
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid Parameters",
  "message": "Agent ID or status is invalid or missing"
}
\`\`\`

---

## Security Note
- Only authorized users may update agent status.
- Always use HTTPS.
`;
export const ticketTypeListMarkdown = `
## View Ticket Type List

Retrieve all available ticket types for the current tenant.

## Ticket Type List Endpoint

\`\`\`
+--------------------------------------------------+
|               Get Ticket Type List               |
+--------------------------------------------------+
| tenant-code:    [vizionsys]   (header)           |
| Authorization:  [Bearer <token>] (header)        |
|              [ Get Ticket Types ]                |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  

---

## What Happens On Request?

1. The frontend sends a GET request for all ticket type records.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/TicketType' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with a list of ticket types.

---

## Example Success Response

\`\`\`json
[
  {
    "ticketTypeId": 1,
    "name": "Incident",
    "description": "User-submitted incidents",
    "isActive": true
  },
  {
    "ticketTypeId": 2,
    "name": "Request",
    "description": "Service requests",
    "isActive": true
  }
  // ...more ticket types
]
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- Only authorized users can view ticket types.
- Always use HTTPS.
`;
export const ticketTypeGetByIdMarkdown = `
## View Ticket Type by ID

Fetch details for a specific ticket type by its ID.

## Ticket Type Details Endpoint

\`\`\`
+--------------------------------------------------+
|             Get Ticket Type By ID                |
+--------------------------------------------------+
| TicketType ID: [50]      (in URL path)           |
| tenant-code:  [vizionsys] (header)               |
| Authorization: [Bearer <token>] (header)         |
|          [ Get Ticket Type Details ]             |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`ticketTypeId\` (path parameter)  
- \`tenant-code\` (header)  
- \`Authorization\` (Bearer token, header)

---

## What Happens On Request?

1. The frontend sends a GET request for a specific ticket type by ID.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/TicketType/50' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend returns details for that ticket type.

---

## Example Success Response

\`\`\`json
{
  "ticketTypeId": 50,
  "name": "Task",
  "description": "A task ticket type",
  "isActive": true
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Ticket Type Not Found",
  "message": "No ticket type exists with the specified ID"
}
\`\`\`

---

## Security Note
- Only authenticated users can view ticket type information.
- Always use HTTPS.
`;

export const ticketTypeUpdateMarkdown = `
## Update Ticket Type By ID

To update a ticket type, send an **encrypted** payload via a PUT request to the endpoint below, with all required headers.

## Ticket Type Update Endpoint

\`\`\`
+--------------------------------------------------------+
|              Update Ticket Type By ID                  |
+--------------------------------------------------------+
| TicketType ID: [50]           (in URL path)            |
| tenant-code:   [vizionsys]     (header)                |
| Content-Type:  [text/plain]     (header)               |
| Authorization: [Bearer <token>] (header)               |
| Body:         [Encrypted Payload]                      |
|               [ Update Ticket Type ]                   |
+--------------------------------------------------------+
\`\`\`

**Required:**  
- \`ticketTypeId\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  
- \`Content-Type:\` \`text/plain\`
- Body must be an encrypted JSON string

---

## What Happens On Request?

1. The frontend sends updated ticket type details, encrypted, as the request body.
2. Example:
\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/TicketType/50' \\
  --header 'Tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`
3. If successful, the backend responds with the updated ticket type object.

---

## Example Decrypted Payload

\`\`\`json
{
  "name": "Incident",
  "displayName": "Incident34",
  "description": null
}
\`\`\`

---

## Example Success Response

\`\`\`json
{
  "ticketTypeId": 50,
  "name": "Task",
  "description": "Task ticket type updated",
  "isActive": true,
  "updatedAt": "2025-07-18T16:10:00.000Z"
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body is missing required fields or not encrypted"
}
\`\`\`

---

## Security Note
- Payloads must be encrypted on the client.
- Use HTTPS at all times.
- Only authorized users can update ticket types.
`;

export const getCategoryListmarkdown = `
## View Active Categories (Paginated)

Retrieve a paginated list of all active categories.

## Categories List Endpoint

\`\`\`
+-----------------------------------------------------------+
|                  Get Categories List (v2)                 |
+-----------------------------------------------------------+
| Query Parameters:                                         |
|   - page: [1] (optional, default = 1)                    |
|   - count: [50] (optional, default = 50)                 |
|   - isActive: [true] (optional, filters active categories)|
| Headers:                                                 |
|   - tenant-code: [vizionsys]                             |
|   - Authorization: [Bearer <token>]                      |
|                                                         |
|                     [ Get Categories ]                   |
+-----------------------------------------------------------+
\`\`\`

**Optional query parameters:**  
- \`page\` (number) - Page number to fetch, defaults to \`1\`.  
- \`count\` (number) - Number of categories per page, defaults to \`50\`.  
- \`isActive\` (boolean) - Filter only active categories if \`true\`.  

---

## What Happens On Request?

1. The frontend sends a GET request with optional pagination and filter params.
2. Example curl request:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Category/v2?page=1&count=50&isActive=true' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with a paginated list of active categories.

---

## Example Success Response

\`\`\`json
{
  "items": [
    {
      "categoryId": 1,
      "name": "Category 1",
      "isActive": true,
      "description": "Example category description"
    }
    // more categories...
  ],
  "pagination": {
    "page": 1,
    "count": 50,
    "totalItems": 150,
    "totalPages": 3
  }
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Security Note
- Only authenticated users can view categories.
- Always use HTTPS.
`;


export const categoryByIdMarkdown = `
## View Category Details by ID

Fetch details of a specific category using its unique ID.

## Category Details Endpoint

\`\`\`
+--------------------------------------------------+
|                Get Category By ID                |
+--------------------------------------------------+
| Category ID:   [58]           (in URL path)       |
| tenant-code:   [vizionsys]     (header)           |
| Authorization: [Bearer <token>] (header)          |
|             [ Get Category Details ]             |
+--------------------------------------------------+
\`\`\`

**Required:**  
- \`categoryId\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)

---

## What Happens On Request?

1. The frontend sends a GET request for a category by ID.
2. Example:
\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Category/58' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`
3. If successful, the backend responds with category details.

---

## Example Success Response

\`\`\`json
{
  "categoryId": 58,
  "name": "Sample Category",
  "isActive": true,
  "description": "Description of the sample category"
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Not Found",
  "message": "No category found with the specified ID"
}
\`\`\`

---

## Security Note
- Only authenticated users can view category details.
- Always use HTTPS.
`;
export const updateCategoryMarkdown = `
## Update Category By ID

To update a category, send an **encrypted** payload via a PUT request to the endpoint below, including all required headers.

## Category Update Endpoint

\`\`\`
+--------------------------------------------------------+
|                Update Category By ID                   |
+--------------------------------------------------------+
| Category ID:   [58]             (in URL path)           |
| tenant-code:   [vizionsys]       (header)              |
| Content-Type:  [text/plain]       (header)             |
| Authorization: [Bearer <token>]   (header)             |
| Body:         [Encrypted Payload]                      |
|                [ Update Category ]                     |
+--------------------------------------------------------+
\`\`\`

**Required:**  
- \`categoryId\` (path parameter)  
- \`tenant-code\` (request header)  
- \`Authorization\` (Bearer token, request header)  
- \`Content-Type:\` \`text/plain\`  
- Body must be an encrypted JSON string

---

## What Happens On Request?

1. The frontend sends updated category details, encrypted, as the request body.
2. Example:
\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Category/58' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`
3. If successful, the backend responds with the updated category object.

---

## Example Decrypted Payload

\`\`\`json
{
  "name": "Updated Category Name",
  "description": "Updated description for the category",
  "isActive": true,
  "updatedAt": "2025-07-18T16:10:00.000Z"
}
\`\`\`

---

## Example Success Response

\`\`\`json
{
  "categoryId": 58,
  "name": "Updated Category Name",
  "description": "Updated description for the category",
  "isActive": true,
  "updatedAt": "2025-07-18T16:10:00.000Z"
}
\`\`\`

## Example Error Response

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body is missing required fields or not encrypted"
}
\`\`\`

---

## Security Note
- Payloads must be encrypted on the client.
- Use HTTPS at all times.
- Only authorized users can update categories.
`;
export const deleteCategoryMarkdown = `
## Delete Category By ID

Deletes a category identified by its ID.

### Endpoint

\`\`\`
DELETE /gateway/Category/:categoryId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Path Parameter

- \`categoryId\` (number): ID of the category to delete

### CURL Example

\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Category/2956' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response

\`\`\`json
{
  "message": "Category deleted successfully",
  "deletedId": 2956
}
\`\`\`

### Error Responses

\`\`\`json
{
  "error": "Not Found",
  "message": "Category with specified ID does not exist"
}
\`\`\`

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Security Note

- Only authorized users should delete categories.
- Always use HTTPS for secure communication.
`;
export const priorityListMarkdown = `
## View Priority List

Retrieve all available priorities for the current tenant.

### Endpoint

\`\`\`
GET /gateway/Priority
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### CURL Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Priority' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
[
  {
    "priorityId": 1,
    "name": "High",
    "description": "High priority tickets",
    "ranking": 1,
    "isActive": true
  },
  {
    "priorityId": 2,
    "name": "Medium",
    "description": "Medium priority tickets",
    "ranking": 2,
    "isActive": true
  },
  {
    "priorityId": 3,
    "name": "Low",
    "description": "Low priority tickets",
    "ranking": 3,
    "isActive": true
  }
]
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Security Note

- Only authenticated users can view the priority list.
- Always use HTTPS for secure communication.
`;
export const createPriorityMarkdown = `
## Create New Priority

To add a new priority, send an encrypted payload as text/plain in a POST request.

### Endpoint

\`\`\`
POST /gateway/Priority
\`\`\`

### Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>

### Request Body

Encrypted JSON payload. Example decrypted data:

\`\`\`json
{
  "name": "High",
  "description": "High priority for urgent tickets",
  "ranking": 1,
  "isActive": true
}
\`\`\`

### Curl Example

\`\`\`bash
curl --location --request POST 'https://simplisysapi.vizionsys.com/gateway/Priority' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "priorityId": 101,
  "name": "High",
  "description": "High priority for urgent tickets",
  "ranking": 1,
  "isActive": true
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body missing required fields or not properly encrypted"
}
\`\`\`

### Security

- Payload must be encrypted before sending.  
- Use HTTPS at all times.  
- Only authorized users can create priorities.
`;
export const getPriorityByIdMarkdown = `
## View Priority Details by ID

Fetch details for a specific priority using its unique ID.

### Endpoint

\`\`\`
GET /gateway/Priority/:priorityId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Path Parameter

- \`priorityId\` (number): ID of the priority to retrieve

### Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Priority/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "priorityId": 1,
  "name": "High",
  "description": "High priority for urgent tickets",
  "ranking": 1,
  "isActive": true
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Not Found",
  "message": "Priority with specified ID does not exist"
}
\`\`\`

### Security Note

- Only authenticated users can view priority details.
- Always use HTTPS.
`;
export const updatePriorityMarkdown = `
## Update Priority By ID

To update a priority, send an **encrypted** JSON payload as text/plain via a PUT request.

### Endpoint

\`\`\`
PUT /gateway/Priority/:priorityId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>  

### Path Parameters

- \`priorityId\` (number): ID of the priority to update

### Request Body

Encrypted JSON payload (sent as \`text/plain\`). Example decrypted payload:

\`\`\`json
{
  "name": "Urgent",
  "description": "Urgent priority for critical tickets",
  "ranking": 1,
  "isActive": true,
  "updatedAt": "2025-07-18T16:10:00.000Z"
}
\`\`\`

### Curl Example

\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Priority/68' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "priorityId": 68,
  "name": "Urgent",
  "description": "Urgent priority for critical tickets",
  "ranking": 1,
  "isActive": true,
  "updatedAt": "2025-07-18T16:10:00.000Z"
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body missing required fields or not encrypted properly"
}
\`\`\`

### Security Note

- Ensure payload is encrypted before sending.  
- Use HTTPS at all times.  
- Only authorized users can update priority entries.
`;

export const deletePriorityMarkdown = `
## Delete Priority By ID

Delete the priority identified by its ID.

### Endpoint

\`\`\`
DELETE /gateway/Priority/:priorityId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Path Parameter

- \`priorityId\`: number — ID of the priority to delete

### CURL Example

\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Priority/68' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response

\`\`\`json
{
  "message": "Priority deleted successfully",
  "deletedId": 68
}
\`\`\`

### Error Responses

\`\`\`json
{
  "error": "Not Found",
  "message": "Priority with specified ID does not exist"
}
\`\`\`

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Security Note
- Only authorized users may delete priorities.  
- Always use HTTPS.
`;


export const getWorkingTimePlanMarkdown = `
## View Working Time Plan List

Retrieve all working time plans available for the current tenant.

### Endpoint

\`\`\`
GET /gateway/WorkingTimePlan
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>  

### Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/WorkingTimePlan' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
[
  {
    "planId": 1,
    "name": "Standard 9-5",
    "description": "Standard working hours from 9 AM to 5 PM",
    "isActive": true,
    "workingDays": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "startTime": "09:00",
    "endTime": "17:00"
  },
  {
    "planId": 2,
    "name": "Flexible",
    "description": "Flexible working hours",
    "isActive": true,
    "workingDays": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "startTime": "08:00",
    "endTime": "18:00"
  }
]
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Security Note

- Only authorized users can view working time plans.
- Always use HTTPS for secure communication.
`;

export const getWorkingTimePlanByIdMarkdown = `
## View Working Time Plan Details by ID

Fetch details for a specific working time plan using its unique ID.

### Endpoint

\`\`\`
GET /gateway/WorkingTimePlan/:planId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Path Parameter

- \`planId\` (number): ID of the working time plan to retrieve

### Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/WorkingTimePlan/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "planId": 1,
  "name": "Standard 9-5",
  "description": "Standard working hours from 9 AM to 5 PM",
  "isActive": true,
  "workingDays": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  "startTime": "09:00",
  "endTime": "17:00"
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Not Found",
  "message": "Working time plan with specified ID does not exist"
}
\`\`\`

### Security Note

- Only authenticated users can view working time plan details.
- Always use HTTPS.
`;
export const updateWorkingTimePlanMarkdown = `
## Update Working Time Plan By ID

Send an encrypted JSON payload as \`text/plain\` to update a working time plan.

### Endpoint

\`\`\`
PUT /gateway/WorkingTimePlan/:planId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>

### Path Parameter

- \`planId\` (number): Working time plan ID

### Request Body (Decrypted Example)

\`\`\`json
{
  "name": "Standard 9-5 Updated",
  "description": "Updated standard working hours",
  "isActive": true,
  "workingDays": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  "startTime": "09:00",
  "endTime": "17:00",
  "updatedAt": "2025-07-18T16:10:00.000Z"
}
\`\`\`

### Curl Example

\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/WorkingTimePlan/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "planId": 1,
  "name": "Standard 9-5 Updated",
  "description": "Updated standard working hours",
  "isActive": true,
  "workingDays": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  "startTime": "09:00",
  "endTime": "17:00",
  "updatedAt": "2025-07-18T16:10:00.000Z"
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body missing required fields or not encrypted"
}
\`\`\`

### Security Note

- Ensure the request body is encrypted before sending.  
- Use HTTPS for all API calls.  
- Only authorized users can update working time plans.
`;
export const createWorkingTimePlanMarkdown = `
## Create New Working Time Plan

Create a new working time plan by sending an encrypted JSON payload.

### Endpoint

\`\`\`
POST /gateway/WorkingTimePlan
\`\`\`

### Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>

### Request Body (Decrypted Example)

\`\`\`json
{
  "name": "New Flexible Plan",
  "description": "Flexible working hours plan",
  "isActive": true,
  "workingDays": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  "startTime": "08:00",
  "endTime": "18:00"
}
\`\`\`

### Curl Example

\`\`\`bash
curl --location --request POST 'https://simplisysapi.vizionsys.com/gateway/WorkingTimePlan' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "planId": 123,
  "name": "New Flexible Plan",
  "description": "Flexible working hours plan",
  "isActive": true,
  "workingDays": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  "startTime": "08:00",
  "endTime": "18:00"
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Missing required fields or payload not encrypted"
}
\`\`\`

### Security Note

- Payloads must be encrypted before sending.  
- Use HTTPS to secure communication.  
- Only authorized users can create working time plans.
`;

export const deleteWorkingTimePlanMarkdown = `
## Delete Working Time Plan By ID

Delete the working time plan identified by its ID.

### Endpoint

\`\`\`
DELETE /gateway/WorkingTimePlan/:planId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Path Parameter

- \`planId\`: number — ID of the working time plan to delete

### CURL Example

\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/WorkingTimePlan/24' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response

\`\`\`json
{
  "message": "Working time plan deleted successfully",
  "deletedId": 24
}
\`\`\`

### Error Responses

\`\`\`json
{
  "error": "Not Found",
  "message": "Working time plan with specified ID does not exist"
}
\`\`\`

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Security Note
- Only authorized users may delete working time plans.  
- Always use HTTPS.
`;

export const getTicketStatusListMarkdown = `
## View Ticket Status List

Retrieve all available ticket statuses for the current tenant.

### Endpoint

\`\`\`
GET /gateway/TicketStatus
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### CURL Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/TicketStatus' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
[
  {
    "statusId": 1,
    "name": "Open",
    "description": "Ticket is open",
    "isActive": true
  },
  {
    "statusId": 2,
    "name": "In Progress",
    "description": "Ticket is being worked on",
    "isActive": true
  },
  {
    "statusId": 3,
    "name": "Resolved",
    "description": "Ticket issue resolved",
    "isActive": true
  },
  {
    "statusId": 4,
    "name": "Closed",
    "description": "Ticket closed",
    "isActive": true
  }
]
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Security Note

- Only authenticated users can view ticket statuses.
- Always use HTTPS.
`;

export const getContactsMarkdown = `
## Get Contacts List

Retrieve a paginated and filtered list of contacts.

### Endpoint

\`\`\`
GET /gateway/Contacts?page=1&count=50&isActive=true
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Query Parameters

- \`page\` (number, optional): Page number, e.g. 1
- \`count\` (number, optional): Number of contacts per page, e.g. 50
- \`isActive\` (boolean, optional): Filter for active contacts, e.g. true

### Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Contacts?page=1&count=50&isActive=true' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "total": 1250,
  "page": 1,
  "count": 50,
  "contacts": [
    {
      "contactId": 101,
      "name": "John Doe",
      "email": "john.doe@example.com",
      "phone": "+911234567890",
      "isActive": true
    },
    {
      "contactId": 102,
      "name": "Jane Smith",
      "email": "jane.smith@example.com",
      "phone": "+911234567891",
      "isActive": true
    }
    // ...more contacts
  ]
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Security Note

- Only authorized users can access contact data.  
- Always use HTTPS for API calls.
`;

export const createContactMarkdown = `
## Create New Contact

Create a new contact by sending an encrypted JSON payload as \`text/plain\`.

### Endpoint

\`\`\`
POST /gateway/Contacts
\`\`\`

### Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>

### Request Body (Decrypted Example)

\`\`\`json
{
  "name": "John Doe",
  "email": "john.doe@example.com",
  "phone": "+911234567890",
  "isActive": true
}
\`\`\`

### Curl Example

\`\`\`bash
curl --location --request POST 'https://simplisysapi.vizionsys.com/gateway/Contacts' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "contactId": 201,
  "name": "John Doe",
  "email": "john.doe@example.com",
  "phone": "+911234567890",
  "isActive": true
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body missing required fields or not properly encrypted"
}
\`\`\`

### Security

- Payload must be encrypted before sending.  
- Use HTTPS at all times.  
- Only authorized users can create contacts.
`;


export const getContactByIdMarkdown = `
## Get Contact By ID

Fetch details for a single contact by their unique ID.

### Endpoint

\`\`\`
GET /gateway/Contacts/:contactId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Path Parameter

- \`contactId\` (number, required): The unique contact ID to fetch (e.g., 258102)

### Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Contacts/258102' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "contactId": 258102,
  "name": "John Doe",
  "email": "john.doe@example.com",
  "phone": "+911234567890",
  "isActive": true
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Not Found",
  "message": "Contact with specified ID does not exist"
}
\`\`\`

### Security Note

- Only authorized users can fetch contact details.  
- Always use HTTPS for API calls.
`;

export const updateContactByIdMarkdown = `
## Update Contact By ID

Update an existing contact's details by sending an encrypted JSON payload as \`text/plain\`.

### Endpoint

\`\`\`
PUT /gateway/Contacts/:contactId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>

### Path Parameter

- \`contactId\` (number, required): The unique contact ID to update (e.g., 258102)

### Request Body (Decrypted Example)

\`\`\`json
{
  "name": "Jane Smith",
  "email": "jane.smith@example.com",
  "phone": "+911234567891",
  "isActive": true
}
\`\`\`

### Curl Example

\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Contacts/258102' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "contactId": 258102,
  "name": "Jane Smith",
  "email": "jane.smith@example.com",
  "phone": "+911234567891",
  "isActive": true
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Invalid Payload",
  "message": "Request body missing required fields or not properly encrypted"
}
\`\`\`

### Security

- Payload must be encrypted before sending.  
- Use HTTPS at all times.  
- Only authorized users can update contacts.
`;

export const changeContactStatusMarkdown = `
## Change Contact Status (Activate/Deactivate in Bulk)

Update the \`isActive\` status of one or more contacts by their IDs.

### Endpoint

\`\`\`
DELETE /gateway/Contacts/status/change?ids=<ids>&status=<true|false>
\`\`\`

### Headers

- Tenant-code: vizionsys  
- Authorization: Bearer <token>

### Query Parameters

- \`ids\` (string, required): Comma-separated list of contact IDs  
  (e.g. \`258102,258103\`)
- \`status\` (boolean, required): \`true\` to activate, \`false\` to deactivate

### Curl Example

\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Contacts/status/change?ids=258102&status=false' \\
  --header 'Tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "message": "Contact status updated successfully",
  "updatedIds": [258102]
}
\`\`\`

### Error Response Example

\`\`\`json
{
  "error": "Bad Request",
  "message": "Invalid IDs or status"
}
\`\`\`

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Notes

- This API is typically used for bulk (multiple IDs) deactivation or activation of contacts.
- Use HTTPS for all API requests.
- Only authorized admins can update user status.
`;

export const AssetsMarkdowns = `
## Get Asset List

Retrieve the list of assets with pagination and optional active status filtering.

### Endpoint

\`\`\`
GET /gateway/Asset?page=0&count=50&isActive=true
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Query Parameters

- \`page\` (number, optional): Page number, e.g. 0  
- \`count\` (number, optional): Number of assets per page, e.g. 50  
- \`isActive\` (boolean, optional): Filter for active assets, e.g. true

### Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Asset?page=0&count=50&isActive=true' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "total": 500,
  "page": 0,
  "count": 50,
  "assets": [
    {
      "assetId": 1001,
      "name": "Laptop Dell XPS 13",
      "category": "Electronics",
      "isActive": true,
      "purchaseDate": "2023-05-10"
    }
  ]
}
\`\`\`

---

## Get Asset By ID

Fetch details of a specific asset by its unique ID.

### Endpoint

\`\`\`
GET /gateway/Asset/:assetId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Path Parameter

- \`assetId\` (number, required): The unique asset ID to fetch (e.g., 1)

### Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Asset/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "assetId": 1,
  "name": "Laptop Dell XPS 13",
  "category": "Electronics",
  "isActive": true,
  "purchaseDate": "2023-05-10"
}
\`\`\`

---

## Update Asset By ID

Update an existing asset by sending encrypted JSON payload as \`text/plain\`.

### Endpoint

\`\`\`
PUT /gateway/Asset/:assetId
\`\`\`

### Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>

### Path Parameter

- \`assetId\` (number, required): The unique asset ID to update (e.g., 1)

### Request Body (Decrypted Example)

\`\`\`json
{
  "name": "Laptop Dell XPS 15",
  "category": "Electronics",
  "isActive": true,
  "purchaseDate": "2023-08-01"
}
\`\`\`

### Curl Example

\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Asset/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "assetId": 1,
  "name": "Laptop Dell XPS 15",
  "category": "Electronics",
  "isActive": true,
  "purchaseDate": "2023-08-01"
}
\`\`\`

---

## Change Asset Status (Activate/Deactivate in Bulk)

Update the \`isActive\` status of one or more assets by their IDs.

### Endpoint

\`\`\`
DELETE /gateway/Asset/status/change?ids=<ids>&status=<true|false>
\`\`\`

### Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

### Query Parameters

- \`ids\` (string, required): Comma-separated list of asset IDs  
  (e.g., \`234,235\`)  
- \`status\` (boolean, required): \`true\` to activate, \`false\` to deactivate

### Curl Example

\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Asset/status/change?ids=234&status=false' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

### Success Response Example

\`\`\`json
{
  "message": "Asset statuses updated successfully",
  "updatedIds": [234]
}
\`\`\`

### Error Response Examples

\`\`\`json
{
  "error": "Bad Request",
  "message": "Invalid IDs or status"
}
\`\`\`

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

### Notes

- Use HTTPS for all API requests.  
- Only authorized users can update asset status.
`;
export const ticketMarkdowns = `
# API Documentation

## Overview

This documentation provides a consistent and clear framework for understanding and using the API endpoints. It includes endpoint descriptions, authentication requirements, parameters, request and response examples to help developers quickly integrate and use the API effectively.

---

## Authentication

All API requests require the following headers:

- \`tenant-code: vizionsys\`  
- \`Authorization: Bearer <your_token_here>\`  

---

## Asset API

### Get Asset List

Retrieve paginated assets, optionally filtered by active status.

\`\`\`
GET /gateway/Asset?page=0&count=50&isActive=true
\`\`\`

**Headers:**

- tenant-code: vizionsys  
- Authorization: Bearer <token>  

**Query Parameters:**

- \`page\` (number, optional): Page number, e.g. 0  
- \`count\` (number, optional): Items per page, e.g. 50  
- \`isActive\` (boolean, optional): Filter by active assets, e.g. true  

**Curl Example:**

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Asset?page=0&count=50&isActive=true' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

**Success Response Example:**

\`\`\`json
{
  "total": 500,
  "page": 0,
  "count": 50,
  "assets": [
    {
      "assetId": 1001,
      "name": "Laptop Dell XPS 13",
      "category": "Electronics",
      "isActive": true,
      "purchaseDate": "2023-05-10"
    }
  ]
}
\`\`\`

---

### Get Asset By ID

Fetch details for a single asset.

\`\`\`
GET /gateway/Asset/:assetId
\`\`\`

**Headers:**

- tenant-code: vizionsys  
- Authorization: Bearer <token>  

**Path Parameter:**

- \`assetId\` (number, required): Asset ID, e.g. 1  

**Curl Example:**

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Asset/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

**Success Response Example:**

\`\`\`json
{
  "assetId": 1,
  "name": "Laptop Dell XPS 13",
  "category": "Electronics",
  "isActive": true,
  "purchaseDate": "2023-05-10"
}
\`\`\`

---

### Update Asset By ID

Update an asset with encrypted JSON sent as \`text/plain\`.

\`\`\`
PUT /gateway/Asset/:assetId
\`\`\`

**Headers:**

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>  

**Path Parameter:**

- \`assetId\` (number, required): Asset ID, e.g. 1  

**Request Body (Decrypted Example):**

\`\`\`json
{
  "name": "Laptop Dell XPS 15",
  "category": "Electronics",
  "isActive": true,
  "purchaseDate": "2023-08-01"
}
\`\`\`

**Curl Example:**

\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Asset/1' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<encrypted_payload>'
\`\`\`

---

### Change Asset Status (Activate/Deactivate)

Bulk update status of assets by IDs.

\`\`\`
DELETE /gateway/Asset/status/change?ids=<ids>&status=<true|false>
\`\`\`

**Headers:**

- tenant-code: vizionsys  
- Authorization: Bearer <token>  

**Query Parameters:**

- \`ids\` (string, required): Comma-separated asset IDs, e.g. \`234,235\`  
- \`status\` (boolean, required): \`true\` to activate, \`false\` to deactivate  

**Curl Example:**

\`\`\`bash
curl --location --request DELETE 'https://simplisysapi.vizionsys.com/gateway/Asset/status/change?ids=234&status=false' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Authorization: Bearer <token>'
\`\`\`

**Success Response Example:**

\`\`\`json
{
  "message": "Asset statuses updated successfully",
  "updatedIds": [234]
}
\`\`\`

---

## Ticket API

### Create or Update Ticket

Create a new ticket or update an existing one.

\`\`\`
POST /gateway/Ticket
\`\`\`

**Headers:**

- tenant-code: vizionsys  
- Content-Type: application/json  
- Authorization: Bearer <token>  

**Request Body:** JSON payload like below example

\`\`\`json
{
  "id": 0,
  "sourceCode": "Phone",
  "ticketLogs": {
    "contactId": 11,
    "title": "postman ticket",
    "priorityId": 1,
    "categoryId": 2168,
    "groupId": 1,
    "agentId": 1,
    "statusId": 3,
    ...
  },
  "ticketAssetList": [],
  "ticketTaskList": [],
  "totalEffort": "00:00:00"
}
\`\`\`

**Curl Example:**

\`\`\`bash
curl --location --request POST 'https://simplisysapi.vizionsys.com/gateway/Ticket' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer <token>' \\
  --data '<json_payload>'
\`\`\`

---
`;

export const createTicketMarkdown = `
# Create Ticket

Create a new ticket by sending ticket details as an encrypted or plain payload.

To create a new ticket, fill out the form and click **Create Ticket**.

## Ticket Creation Form

\`\`\`
+-------------------------------+
|        New Ticket Form        |
+-------------------------------+
| Title:         [___________]  |
| Description:   [___________]  |
| Contact ID:    [___]          |
| Secondary ID:  [___]          |
| Priority ID:   [___]          |
| Ticket Type:   [___]          |
| Due Date:      [___]          |
| Group ID:      [___]          |
| Agent ID:      [___]          |
| Status ID:     [___]          |
| Tag:           [___________]  |
|      [Create Ticket]          |
+-------------------------------+
\`\`\`

**Required fields:**  
- Title, Description, Contact ID, Priority ID, Ticket Type, Due Date, Group ID, Agent ID, Status ID

---

## What Happens After Submit?

1. The user fills the form and clicks **Create Ticket**.
2. The frontend sends a POST request to the backend:

## Endpoint

\`\`\`
POST /gateway/Ticket
\`\`\`

## Headers

- tenant-code: vizionsys  
- Content-Type: text/plain  
- Authorization: Bearer <token>

## Request Body (Example, decrypted JSON format)

\`\`\`json
{
  "id": 0,
  "sourceCode": "Phone",
  "ticketLogs": {
    "contactId": 11,
    "secondaryContactId": 0,
    "priorityId": 1,
    "title": "postman ticket",
    "categoryId": 2168,
    "ticketLogChangesList": [],
    "priority": {
      "id": 1,
      "name": "P1",
      "color": "#ec0909",
      "ticketTypeList": [
        {
          "id": 710,
          "priorityId": 1,
          "ticketTypeId": 51,
          "ticketType": {
            "id": 51,
            "parentId": 1,
            "name": "Service Request",
            "displayName": "Service Request"
          }
        }
      ]
    },
    "source": {
      "id": 8,
      "code": "Phone",
      "displayName": "Phone",
      "createdAt": "2024-01-10T09:01:22.445784",
      "updatedAt": "2025-02-04T11:45:31.084551",
      "isActive": true,
      "label": "Phone",
      "value": 8
    },
    "ticketType": {
      "id": 51,
      "parentId": 1,
      "name": "Service Request",
      "displayName": "Service Request",
      "description": null,
      "isActive": true,
      "json": null,
      "label": "Service Request",
      "value": 51
    },
    "contacts": {
      "label": "Amith. Malipatil,(malipatilamith404@gmail.com)",
      "name": "Amith. Malipatil",
      "value": "Amith. Malipatil",
      "id": 11
    },
    "secondaryContacts": null,
    "groupId": 1,
    "agentId": 1,
    "statusId": 3,
    "note": "",
    "effort": "00:00:00",
    "ticketLogAttachmentsList": [],
    "circulationListTO": "malipatilamith404@gmail.com",
    "circulationListBCC": null,
    "circulationListCC": null,
    "IsNotePrivate": false,
    "group": {
      "id": 1,
      "name": "1st Line",
      "ticketAssignWithin": null,
      "escalationEmailTo": null,
      "groupDescription": null,
      "ranking": 0,
      "isActive": true,
      "createdAt": "2024-01-10T09:01:22.445784",
      "updatedAt": "2025-05-08T07:15:52.868795",
      "userGroupList": [
        {
          "id": 618184,
          "userId": 78,
          "groupId": 1,
          "isDefault": false,
          "isActive": true,
          "agent": {
            "id": 78,
            "firstName": "Harsha",
            "lastName": "Patel",
            "userName": "hars8762",
            "agentTypeId": 0
          }
        }
      ],
      "label": "1st Line",
      "value": 1,
      "agentsList": [
        {
          "id": 77,
          "firstName": "Sanjay",
          "lastName": "Kulkarni",
          "userName": "sanj2021",
          "agentTypeId": 0,
          "agentAvatarPath": "https://simplisysapi.vizionsys.com/gateway/imagedetails/vizionsys/Agent/77/sanj2021.png",
          "mobilePhone": "6566467677676",
          "email": "girish.kulkarni@vizionsys.com",
          "isAdmin": false,
          "isActive": true,
          "language": "676777u756767676"
        }
      ]
    },
    "ticketStatus": {
      "id": 3,
      "name": "InProgress",
      "displayName": "In Progress",
      "type": "TicketStatus",
      "isActive": true,
      "ranking": 999,
      "label": "In Progress",
      "value": 3
    },
    "outComeCategoryId": null,
    "agent": {
      "id": 1,
      "firstName": "Amith Malipatil (vizion123)",
      "email": null
    },
    "downtime": "00:00:00",
    "thirdPartyRefNo": null,
    "contactOrganisationId": 0
  },
  "ticketAssetList": [],
  "ticketRelationshipList": null,
  "ticketTagList": null,
  "ticketTaskList": [],
  "totalEffort": "00:00:00"
}
\`\`\`

## Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Ticket' \\
  --header 'tenant-code: vizionsys' \\
  --header 'Content-Type: text/plain' \\
  --header 'Authorization: Bearer <token>' \\
  --data-raw '<encrypted or raw JSON payload>'
\`\`\`

## Response

- **Success (201 Created)**: Returns newly created ticket data and ID.  
- **Error (400 Bad Request)**: Invalid or missing fields.  
- **Error (401 Unauthorized)**: Missing or invalid authorization.

---

## Notes

- Replace \`<token>\` with your valid Bearer token.  
- Payload usually encrypted and sent as \`text/plain\` content type.  
- Always use HTTPS to secure API requests.
`;


export const updateTicketMarkdown = `
# Update Ticket

Update an existing ticket by sending ticket details as an encrypted or plain payload to the ticket ID endpoint.

## How to Update Ticket

1. Prepare the updated ticket data in the required JSON structure.
2. Send a PUT request to update the ticket by its ID.
3. Ensure all required headers and token authorization.

## Endpoint

\`\`\`
PUT /gateway/Ticket/{id}
\`\`\`

## Headers

- tenant-code: vizionsys
- Content-Type: text/plain
- Authorization: Bearer <token>

## Request Body (Example, decrypted JSON payload)

\`\`\`json
{
  "id": 4454,
  "sourceCode": "Phone",
  "ticketLogs": {
    "id": 51894,
    "ticketId": 4454,
    "circulationListTO": "malipatilamith404@gmail.com",
    "ticketStatus": {
      "id": 3,
      "name": "InProgress",
      "displayName": "In Progress"
    },
    "priority": {
      "id": 1,
      "name": "P1",
      "color": "#ec0909"
    },
    "ticketType": {
      "id": 51,
      "name": "Service Request"
    },
    "group": {
      "id": 1,
      "name": "1st Line"
    },
    "agent": {
      "id": 1,
      "firstName": "Amith Malipatil"
    },
    "contacts": {
      "label": "Amith. Malipatil,(malipatilamith404@gmail.com)"
    }
  },
  "ticketAssetList": [],
  "ticketRelationshipList": null,
  "ticketTagList": [],
  "ticketTaskList": [],
  "totalEffort": "00:00:00"
}
\`\`\`

> Note: Payload can be encrypted and sent as plain text content type.

## Curl Example

\`\`\`bash
curl --location --request PUT 'https://simplisysapi.vizionsys.com/gateway/Ticket/4454' \\
--header 'tenant-code: vizionsys' \\
--header 'Content-Type: text/plain' \\
--header 'Authorization: Bearer <token>' \\
--data-raw '<encrypted or raw JSON payload>'
\`\`\`

## Responses

- **200 OK**: Ticket updated successfully. Returns updated ticket ID.
- **400 Bad Request**: Invalid or missing required fields in the request payload.
- **401 Unauthorized**: Missing or invalid authorization token.
- **404 Not Found**: Ticket ID to update was not found.

---

## Notes

- Replace \`<token>\` with your valid Bearer token.
- Always use HTTPS to secure the requests.
- Verify ticket JSON payload matches the API structure for consistency.
`;

export const getTicketLogAttachmentsMarkdown = `
# Get Ticket Log Attachments By ID

Retrieve attachments related to a specific Ticket Log Attachment ID.

## Endpoint

\`\`\`
GET /gateway/TicketLogAttachments/{id}
\`\`\`

## Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

## URL Parameter

- **id** (integer, required): The Ticket Log Attachment ID to fetch attachments for.  
  Example: 4455

## Curl Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/TicketLogAttachments/4455' \\
--header 'tenant-code: vizionsys' \\
--header 'Authorization: Bearer <token>'
\`\`\`

## Responses

- **200 OK**: Returns a list of attachments for the specified Ticket Log Attachment ID.

  Example response:
  \`\`\`json
  {
    "message": "Ticket log attachments fetched successfully",
    "attachments": [
      {
        "attachmentId": 123,
        "fileName": "screenshot.png",
        "fileType": "image/png",
        "url": "https://simplisysapi.vizionsys.com/path/to/screenshot.png"
      }
    ]
  }
  \`\`\`

- **400 Bad Request**: Invalid or missing ID parameter.
- **401 Unauthorized**: Missing or invalid Bearer token.
- **404 Not Found**: Ticket Log Attachment ID not found.

---

## Notes

- Replace \`<token>\` with your valid Bearer token.
- Always use HTTPS to secure the requests.
`;

export const getTicketTimelineMarkdown = `# Get Ticket Timeline

Retrieve a paginated list of timeline events for a specific ticket by its ID.

---

## Endpoint

\`\`\`
GET /gateway/Ticket/timeline/{ticketId}?page=0&count=10
\`\`\`

---

## Headers

- **tenant-code**: vizionsys  
- **Authorization**: Bearer <token>

---

## Path Parameter

| Name     | Type    | Required | Description                          |
| -------- | ------- | -------- | ---------------------------------- |
| ticketId | integer | Yes      | The ID of the ticket to fetch timeline for. |

---

## Query Parameters

| Name  | Type    | Required | Description                               |
| ----- | ------- | -------- | --------------------------------------- |
| page  | integer | No       | The page number (zero-based). Default: 0 |
| count | integer | No       | Number of timeline entries per page. Default: 10 |

---

## cURL Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Ticket/timeline/4455?page=0&count=10' \\
--header 'tenant-code: vizionsys' \\
--header 'Authorization: Bearer <token>'
\`\`\`

---

## Responses

### 200 OK

Returns a paginated timeline of events for the specified ticket.

Example response:

\`\`\`json
{
  "page": 0,
  "ticketId": 4455,
  "ticketLog": [
    {
      "id": 51915,
      "ticketId": 4455,
      "customId": null,
      "workflowNodeId": 0,
      "type": "Update",
      "title": "postman ticket",
      "note": "",
      "description": "postman ticket",
      "webformValues": null,
      "thirdPartyRefNo": null,
      "webformId": 0,
      "categoryId": 2168,
      "statusId": 5,
      "ticketTypeId": 51,
      "priorityId": 1,
      "urgency": 0,
      "outgoingSourceCode": null,
      "sourceCode": "Phone",
      "userType": "Agent",
      "sla": 0,
      "openDateTime": "2025-07-21T06:10:15.520218",
      "dueDateTime": "2025-07-21T09:30:00",
      "firstResponseDueDateTime": "2025-07-21T09:20:00",
      "scheduleDateTime": "2025-07-21T06:22:41.68638",
      "createdDateTime": "2025-07-21T06:22:41.68638",
      "updatedDateTime": "2025-07-21T06:22:41.68638",
      "createdByUserId": 1,
      "isActive": true,
      "contactId": 11,
      "secondaryContactId": 0,
      "groupId": 1,
      "contactOrganisationId": 0,
      "agentId": 1,
      "managerId": 0,
      "outComeCategoryId": 0,
      "nextActionDescription": "",
      "from": null,
      "to": null,
      "circulationListTO": null,
      "cc": null,
      "circulationListCC": null,
      "bcc": null,
      "circulationListBCC": null,
      "body": null,
      "isMessageSuccess": true,
      "isNotePrivate": false,
      "user": null,
      "ticketLogAttachmentsList": [],
      "ticketLogChangesList": [
        {
          "id": 16308,
          "ticketLogId": 51915,
          "changes": "TO Changed From malipatilamith404@gmail.com To --"
        }
      ],
      "slaRemainingInTicks": -639712265890,
      "slaRemaining": "-17:46:11.2265890",
      "effortInTicks": 0,
      "effort": "00:00:00",
      "effortInMinutes": 0,
      "downtimeInTicks": 0,
      "downtime": "00:00:00",
      "ticketStatus": null,
      "priority": null,
      "ticketType": null,
      "group": null,
      "agent": {
        "id": 1,
        "firstName": "Amith",
        "lastName": "Malipatil",
        "userName": "vizion123",
        "agentTypeId": 0,
        "agentAvatarPath": "https://simplisysapi.vizionsys.com/gateway/imagedetails/vizionsys/Agent/1/vizion123.jpg",
        "mobilePhone": "",
        "email": null,
        "isAdmin": false,
        "isActive": true,
        "roleId": 1,
        "appDeviceId": null,
        "webDeviceId": ":-fd8Fo2XblxnQLCL90",
        "language": "",
        "agentDescription": null,
        "createdAt": "2025-07-23T10:16:11.2315199Z",
        "updatedAt": "2025-07-23T10:16:11.23152Z",
        "isTwoFactorEnabled": false,
        "roles": null,
        "fullName": "Amith Malipatil"
      },
      "contacts": null,
      "secondaryContacts": null,
      "manager": null,
      "contactOrganisation": null,
      "webform": null,
      "category": null,
      "outComeCategory": null
    }
  ],
  "totalEfforts": "00:00:00",
  "totalDownTime": "00:00:00",
  "slaRemaining": "-17:46:11.2265890"
}
\`\`\`

### 401 Unauthorized

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Notes

- Replace \`<token>\` in the Authorization header with your valid Bearer token.
- Always use HTTPS for secure communication.
- Pagination defaults to \`page=0\` and \`count=10\` if not specified.
`;


export const updateTicketPriorityMarkdown = `
# Update Ticket Priority

Update the priority of a ticket by its ID with a new priority ID.

---

## Endpoint

\`\`\`
POST /gateway/Ticket/update/priority/byticket/{ticketId}?priorityid=2
\`\`\`

---

## Headers

- tenant-code: vizionsys  
- Authorization: Bearer <token>

---

## Path Parameter

| Name     | Type    | Required | Description                  |
|----------|---------|----------|------------------------------|
| ticketId | integer | Yes      | The ID of the ticket to update |

---

## Query Parameter

| Name       | Type    | Required | Description                      |
|------------|---------|----------|----------------------------------|
| priorityid | integer | Yes      | The new priority ID to assign to the ticket |

---

## cURL Example

\`\`\`bash
curl --location 'https://simplisysapi.vizionsys.com/gateway/Ticket/update/priority/byticket/4448?priorityid=2' \\
--header 'tenant-code: vizionsys' \\
--header 'Authorization: Bearer <token>'
\`\`\`

---

## Responses

### 200 OK

Ticket priority updated successfully.

### 401 Unauthorized

\`\`\`json
{
  "error": "Unauthorized",
  "message": "Invalid or missing Bearer token"
}
\`\`\`

---

## Notes

- Replace \`<token>\` with your valid Bearer token.
- Always use HTTPS.
`;
